import { z } from 'zod'

const basicInfoSchema = z.object({
  email: z.email('Invalid email address'),
  username: z.email('Username must be a valid email address'),
  firstName: z
    .string()
    .min(1, 'First name is required')
    .max(100, 'First name must be at most 100 characters'),
  lastName: z
    .string()
    .min(1, 'Last name is required')
    .max(100, 'Last name must be at most 100 characters'),
  password: z.string().min(8, 'Password must be at least 8 characters').optional(),
  temporaryPassword: z.boolean().optional(),
})

const accountStatusSchema = z.object({
  enabled: z.boolean(),
  emailVerified: z.boolean().optional(),
  locked: z.boolean().optional(),
})

export const realmRolesSchema = z.object({
  cs_sys_op: z.boolean().optional(),
  cs_sys_admin: z.boolean().optional(),
  cs_active: z.boolean().optional(),
  cs_locked: z.boolean().optional(),
  cs_systems_all: z.boolean().optional(),
  cs_locations_all: z.boolean().optional(),
  cs_sections_all: z.boolean().optional(),
})

const attributesSchema = z.object({
  systems: z.union([z.string(), z.array(z.string())]).optional(),
  systems_all: z.boolean().optional(),
  dbas: z.union([z.string(), z.array(z.string())]).optional(),
  districts: z.union([z.string(), z.array(z.string())]).optional(),
  locations: z.union([z.string(), z.array(z.string())]).optional(),
  locations_all: z.boolean().optional(),
  sections: z.union([z.string(), z.array(z.string())]).optional(),
  sections_all: z.boolean().optional(),
})

export const createUserSchema = z.object({
  basicInfo: basicInfoSchema.extend({
    password: z.string().min(8, 'Password must be at least 8 characters'),
  }),
  accountStatus: accountStatusSchema,
  realmRoles: realmRolesSchema,
  clientRoles: z.record(z.string(), z.array(z.string())).optional(),
  attributes: attributesSchema,
})

export const updateUserSchema = z.object({
  basicInfo: basicInfoSchema.extend({
    password: z
      .string()
      .transform((val) => (val === '' ? undefined : val))
      .pipe(z.string().min(8, 'Password must be at least 8 characters').optional()),
  }),
  accountStatus: accountStatusSchema,
  realmRoles: realmRolesSchema,
  clientRoles: z.record(z.string(), z.array(z.string())).optional(),
  attributes: attributesSchema,
})

export type RealmRolesFormData = z.infer<typeof realmRolesSchema>
export type CreateUserFormData = z.infer<typeof createUserSchema>
export type UpdateUserFormData = z.infer<typeof updateUserSchema>
export type UserFormInput = z.input<typeof createUserSchema> | z.input<typeof updateUserSchema>
